import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { DxScrollViewModule, DxValidatorModule } from 'devextreme-angular';
import { DxSortableComponent } from 'devextreme-angular/ui/sortable';
import { DxValidatorComponent } from 'devextreme-angular/ui/validator';
import { DxiValidationRuleComponent } from 'devextreme-angular/ui/nested';
import { GpdButtonComponent } from '../../../../../../../components/gpd-button/gpd-button.component';
import { SvgIconComponent } from '../../../../../../../components/svg-icon/svg-icon.component';
import { SetupTaskPackageNodeComponent } from '../../node-types/setup-task-package-node/setup-task-package-node.component';
import { MultiStepFormComponent } from '../../../../../../../components/multi-step-form/multi-step-form.component';
import { Milestone, Phase, TaskPackage } from '../../../../../../../interfaces/entities/project-configuration';

@Component({
    selector: 'app-setup-task-packages-step',
    imports: [
        CommonModule,
        SetupTaskPackageNodeComponent,
        DxSortableComponent,
        DxScrollViewModule,
        DxValidatorComponent,
        DxiValidationRuleComponent,
        DxValidatorModule,
        GpdButtonComponent,
        SvgIconComponent,
    ],
    templateUrl: './setup-task-packages-step.component.html',
    styleUrl: './setup-task-packages-step.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SetupTaskPackagesStepComponent {
  @Input() phases: Phase[] = [];
  @Input() validationGroup: any;
  @Input() multiStepForm?: MultiStepFormComponent;

  @Output() phasesChange = new EventEmitter<Phase[]>();
  @Output() validationUpdate = new EventEmitter<void>();

  // Callbacks
  taskPackageValidationCallbacks: Array<() => void> = [];

  constructor(private cdr: ChangeDetectorRef) {}

  taskPackageAdapterConfig = {
    getValue: () => {
      return this.taskPackageValidationCallback();
    },
    validationRequestsCallbacks: this.taskPackageValidationCallbacks,
  };

  taskPackageValidationCallback = () => {
    // Collect all task packages from all phases and milestones
    const allTaskPackages: TaskPackage[] = [];
    for (const phase of this.phases) {
      if (phase.milestones?.length > 0) {
        for (const milestone of phase.milestones) {
          if (milestone.taskPackages?.length > 0) {
            allTaskPackages.push(...milestone.taskPackages);
          }
        }
      }
    }

    if (allTaskPackages.length === 0) {
      return false;
    }

    // Check each task package for validity (mimic phase validation pattern)
    for (const taskPackage of allTaskPackages) {
      // Check if task package has a name
      if (!taskPackage.name || taskPackage.name.trim() === '') {
        return false;
      }

      // Check for duplicate names (case-insensitive) - exact same pattern as phase validation
      const duplicateExists = allTaskPackages.some(otherTaskPackage => {
        return (
          otherTaskPackage !== taskPackage &&
          otherTaskPackage.name &&
          otherTaskPackage.name.trim().toLowerCase() === taskPackage.name.trim().toLowerCase()
        );
      });

      if (duplicateExists) {
        return false;
      }
    }

    return true;
  };

  // Task package management methods
  onTaskPackageAdd = (milestone: Milestone, event?: any) => {
    const { fromIndex, toIndex, fromData, toData } = event;
    var data = fromData[fromIndex];
    fromData.splice(fromIndex, 1);
    toData.splice(toIndex, 0, data);
    this.phasesChange.emit(this.phases);
    this.cdr.detectChanges();

    this.multiStepForm?.triggerValidationUpdate();
  };

  onTaskPackageCreate(milestone: Milestone) {
    const allTaskPackages = this.phases.flatMap(p => p.milestones?.flatMap(m => m.taskPackages || []) || []);
    const tempId = Math.min(...allTaskPackages.map(tp => tp.id || 0), 0) - 1;

    const newTaskPackage: TaskPackage = {
      id: tempId,
      name: '',
    };

    if (!milestone.taskPackages) {
      milestone.taskPackages = [];
    }

    milestone.taskPackages.push(newTaskPackage);
    this.phasesChange.emit(this.phases);
    this.cdr.detectChanges();

    this.multiStepForm?.triggerValidationUpdate();
    // Force validation group to recognize new components
    if (this.validationGroup) {
      this.multiStepForm?.resetValidation();
    }
  }

  onTaskPackageDelete = (milestone: Milestone, taskPackage: TaskPackage) => {
    if (milestone.taskPackages) {
      const index = milestone.taskPackages.indexOf(taskPackage);
      if (index > -1) {
        milestone.taskPackages.splice(index, 1);
        this.phasesChange.emit(this.phases);
        this.cdr.detectChanges();

        // Trigger validation update after DOM updates
        this.multiStepForm?.triggerValidationUpdate();
      }
    }
  };

  onTaskPackageRemove = (milestone: Milestone, event: any) => {
    if (milestone.taskPackages) {
      // Remove the task package from this milestone
      milestone.taskPackages.splice(event.fromIndex, 1);
      this.phasesChange.emit(this.phases);
      this.cdr.detectChanges();

      this.multiStepForm?.triggerValidationUpdate();
    }
  };

  onTaskPackageReorder = (milestone: Milestone, event: any) => {
    const { fromIndex, toIndex, fromData, toData } = event;

    if (fromIndex !== toIndex && milestone.taskPackages) {
      const data = fromData[fromIndex];
      fromData.splice(fromIndex, 1);
      toData.splice(toIndex, 0, data);
      this.phasesChange.emit([...this.phases]);
      this.cdr.detectChanges();

      // Trigger validation update after reordering
      this.multiStepForm?.triggerValidationUpdate();
    }
  };

  togglePhaseExpansion = (phase: Phase) => {
    // Add expanded property to phase if it doesn't exist
    if (!('expanded' in phase)) {
      (phase as any).expanded = true;
    }

    (phase as any).expanded = !(phase as any).expanded;
    this.cdr.detectChanges();
  };

  isPhaseExpanded = (phase: Phase): boolean => {
    return (phase as any).expanded !== false; // Default to true if not set
  };

  toggleMilestoneExpansion = (milestone: Milestone) => {
    // Add expanded property to milestone if it doesn't exist
    if (!('expanded' in milestone)) {
      (milestone as any).expanded = true;
    }

    (milestone as any).expanded = !(milestone as any).expanded;
    this.cdr.detectChanges();
  };

  isMilestoneExpanded = (milestone: Milestone): boolean => {
    return (milestone as any).expanded !== false; // Default to true if not set
  };

  hasValidMilestones = (phase: Phase): boolean => {
    return phase.milestones && phase.milestones.length > 0;
  };

  hasValidTaskPackages = (milestone: Milestone): boolean => {
    return milestone.taskPackages && milestone.taskPackages.length > 0;
  };

  onTaskPackageNameChange = (taskPackage: TaskPackage, event: any) => {
    taskPackage.name = event.value;
    this.phasesChange.emit(this.phases);
    this.cdr.detectChanges();

    // Trigger validation update
    this.multiStepForm?.triggerValidationUpdate();
  };
}
